// React Core

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactTabulator } from 'react-tabulator';
import type { Tabulator } from 'react-tabulator/lib/types/TabulatorTypes';
import type { RowComponent } from 'tabulator-tables';
import DynamicForm from '@/domains/site-management/components/DynamicForm/DynamicForm';
// Plugin / Utilities
import NoticeMessage from '@/plugin/noticemessage/noticemessage';
import ButtonGroup from '@/shared/components/ui/ButtonGroup/ButtonGroup';
// Components
import ContainerCard from '@/shared/components/ui/ContainerCard/ContainerCard';
import DetailForm from '@/shared/components/ui/DetailForm/DetailForm';
import Filtering from '@/shared/components/ui/Filtering/Filtering';
import GeneralInput from '@/shared/components/ui/GeneralInput/GeneralInput';
import LogList from '@/shared/components/ui/LogList/LogList';
import useCommonCodes from '@/shared/hooks/useCommonCodes';
import useRoadManagement from '@/shared/hooks/useRoadManagement';
// Hooks
import useSiteManagement from '@/shared/hooks/useSiteManagement';
import type { RoadData } from '@/shared/types/types-road-management';
import type { SiteData } from '@/shared/types/types-site-management';
// Breaking up into parts for now
import { boxTabulator, optionsTabulator } from './SiteManagementConstantVariables';

interface RoadInputType {
  site_id?: number; //사이트 id
  road_id?: number; //접근로 id
  name?: string; //접근로 명칭 road_name
  incoming_compass?: string; //진입 방향
  outgoing_compass?: string; //진출 방향
  incoming_lane_cnt?: number; //진입 방향 차선수
  outgoing_lane_cnt?: number; //진출 방향 차선수
  incoming_direction_sub1?: string; //진입방향1
  incoming_direction_sub2?: string; //진입방향2
  incoming_direction_sub3?: string; //진입방향3
  incoming_direction_sub4?: string; //진입방향4
  incoming_direction_sub5?: string; //진입방향5
  incoming_direction_sub6?: string; //진입방향6
  incoming_direction_sub7?: string; //진입방향7
  incoming_direction_sub8?: string; //진입방향8
  incoming_direction_sub9?: string; //진입방향9
  incoming_direction_sub10?: string; //진입방향10
  incoming_direction?: string; //방향 모두 합친 값
  crosswalk?: string; //횡단보도 유무 - 105002
  crosswalk_length?: string | null; //횡단보도 길이
  crosswalk_width?: string | null; //횡단보도 폭
  traffic_light?: string; //보행자 신호등 유무 - 106002
  mapped_detector?: string; //매핑 검지기
  // mapped_vms?: string; //매핑 전광판 - 비활성
  mapped_logojector?: string; //매핑 로고젝터
  mapped_speaker?: string; //매핑 스피커
  is_new?: boolean; //동적박스 생성시에는 새로 추가되는 데이터다
}
interface TSiteId {
  site_id: number | null;
}

// Common code 102 = site type group. 102003 represents 교차로(횡단보도). Matching
// on the code (not the display label) keeps this UI stable even if the Korean
// label is renamed (e.g. to '교차로') later on. If the underlying code value
// itself changes in the seed data, update this constant.
const CROSSROAD_TYPE_CODE = '102003';

const SiteManagement = () => {
  //site input form values, row data to display
  const [siteInputFormValues, setSiteInputFormValues] = useState<SiteData>({
    site_id: 0,
    name: '',
    address: '',
    lat: 0,
    lng: 0,
    type: '',
    mapped_box: '',
    mapped_sensor: '',
    number_road: 0,
    description: '',
    registered_by: '',
    registered_time: '',
    updated_by: '',
    updated_time: '',
  });

  const { t } = useTranslation();

  const [queryParams, setQueryParams] = useState(''); // queryParams 상태
  const [siteId, setSiteId] = useState<number | null>(null); // siteId 상태
  const [selectedSiteId, setSelectedSiteId] = useState<TSiteId | null>({
    //선택된 사이트아이디를 만들기 위한 (하이라이트를 위한)
    site_id: null,
  });
  const selectedSiteIdRef = useRef(selectedSiteId);

  const [newId, setNewId] = useState<number | null>(null);

  //const [selectedSiteId, setSelectedSiteId]= useState(null)
  const [_curRowId, setCurRowId] = useState(null); //현재 선택된 row번호 - Currently selected row number

  const { commonListData } = useCommonCodes({
    optionParams: 'upper-code=102&upper-code=103&upper-code=104&upper-code=105&upper-code=106',
  });

  //입력용 road 목록
  const [roadInputList, setRoadInputList] = useState<Array<RoadInputType>>([]);

  //CRUD Button Group - Button State
  const [buttonState, setButtonState] = useState({
    confirm: true,
    cancel: true,
    delete: true,
    create: false,
  });

  //CRUD Button Group Function
  const disableAllButtons = useCallback(() => {
    setButtonState({
      confirm: true,
      cancel: true,
      delete: true,
      create: true,
    });
  }, []);

  //
  const enableInitialButtons = useCallback(() => {
    disableAllButtons();
    setIsNewClicked(false);
    setButtonState((prevState) => ({
      ...prevState,
      create: false,
    }));
  }, [disableAllButtons]);

  //취소버튼 활성화
  const enableCancelButtons = () => {
    setButtonState((prevState) => ({
      ...prevState,
      cancel: false, //활성화
    }));
  };

  //확인 버튼 비활성화
  const disableConfirmButtons = () => {
    setButtonState((prevState) => ({
      ...prevState,
      confirm: true, //비활성화
    }));
  };

  //수정관련 버튼 활성화
  const enableUpdateButtons = useCallback(() => {
    disableAllButtons();
    setButtonState((prevState) => ({
      ...prevState,
      cancel: false, //disable-false는 활성화
      delete: false, //disable-false는 활성화
      create: false, //disable-false는 활성화
    }));
  }, [disableAllButtons]);

  //site입력폼 비우기
  const emptyDetail = useCallback(() => {
    setSiteInputFormValues((prevValues) => ({
      ...prevValues,
      site_id: 0,
      name: '',
      address: '',
      lat: 0,
      lng: 0,
      type: '',
      number_road: 0,
      mapped_box: '',
      description: '',
    }));

    setRoadInputList([]);
  }, []);
  //102 - 교차로, 횡단보도 , 103 - 방위 (동서남북...) , 104 - 진행방향 , 105 - 횡단보도 여부 , 106 - 신호등 여부
  // 102 - Intersection, Crosswalk
  // 103 - Direction (East, West, South, North...)
  // 104 - Direction of travel
  // 105 - Crosswalk presence
  // 106 - Traffic signal presence
  const reloadCallback = useCallback(() => {
    enableInitialButtons();
    tbRef.current?.deselectRow();
    setIsRequired(false);
    emptyDetail();
    setDisabledForm(true);
    setDisabledId(true);
    setHasChangesUpdate(false);
    setSiteId(null);
    setSelectedSiteId({ site_id: null });
  }, [enableInitialButtons, emptyDetail]);

  // 102에 해당하는 코드와 이름을 가져옵니다 - 102 - 교차로, 횡단보도
  // Retrieve the code and name corresponding to 102 - Intersection, Crosswalk
  const typeOptions = [
    ...(commonListData?.['102']?.code?.map((code, index) => ({
      label: commonListData?.['102']?.name[index] || '', // make typechecker happy
      value: code || '', // make typechecker happy
    })) || []),
  ];

  // 103에 해당하는 코드와 이름을 가져옵니다 - 103 - 방위 (동서남북...)
  // Retrieve the code and name corresponding to 103 - Direction (East, West, South, North...)
  const directionOptions =
    commonListData?.['103']?.code?.map((code, index) => ({
      label: commonListData?.['103']?.name[index] ?? '',
      value: code ?? '',
    })) || [];

  // 105에 해당하는 코드와 이름을 가져옵니다 - 105 - 횡단보도 여부
  // Retrieve the code and name corresponding to 105 - Crosswalk presence
  const crosswalkPresenceOptions =
    commonListData?.['105']?.code?.map((code, index) => ({
      label: commonListData?.['105']?.name[index] ?? '',
      value: code ?? '',
    })) || [];

  // 106에 해당하는 코드와 이름을 가져옵니다 - 106 - 보행자 신호등 유무
  // Retrieve the code and name corresponding to 106 - Presence of pedestrian traffic signal
  const pedestrianSignalPresenceOptions =
    commonListData?.['106']?.code?.map((code, index) => ({
      label: commonListData?.['106']?.name[index] ?? '',
      value: code ?? '',
    })) || [];

  console.log('directionOptions');
  console.log(directionOptions);

  // site 데이터 가져오기
  const { siteListData, detailSiteData, updateSiteRoad, createSite, deleteSiteRoad } = useSiteManagement({
    queryParams: queryParams || '',
    siteId,
    onUpdateSuccess: (responseData: unknown) => {
      //alert('onUpdateSuccess')
      // updateCallback();
      setDisabledForm(false);
      enableUpdateButtons();
      const site_id = (responseData as { site_id?: number })?.site_id ?? null;
      setNewId(site_id);
      //InitSiteRoadInputForm()
    },
    onDeleteSuccess: () => {
      reloadCallback();
    },
    onCreateSuccess: (responseData: unknown) => {
      //alert('onCreateSuccess')
      //reloadCallback();

      const site_id = (responseData as { site_id?: number })?.site_id ?? null;
      setNewId(site_id);
      enableUpdateButtons();
      setIsNewClicked(false);
      setDisabledForm(false);

      requestAnimationFrame(() => {
        nameRef.current?.focus();
      });
    },
  });

  const { roadListData, deleteRoad, createRoad } = useRoadManagement({
    queryParams: siteId ? `site_id=${siteId}` : undefined, // siteId가 있을 때만 API 호출
    onUpdateSuccess: (_responseData) => {
      console.log('onUpdateSuccess');
      //alert('onUpdateSuccess')
    },
    onDeleteSuccess: reloadCallback,
    onCreateSuccess: (_responseData) => {
      console.log('onCreateSuccess');
      //alert('onCreateSuccess')
    },
  });

  console.log('useRoadManagement');
  console.log(roadListData);

  const [_selectedOption, _setSelectedOption] = useState('');

  const languageTabulator = () => {
    const datalanguage = {
      pagination: {
        first: t('cmn > first page'), //text for the first page button
        first_title: t('cmn > first page'), //tooltip text for the first page button
        last: t('cmn > last page'),
        last_title: t('cmn > last page'),
        prev: t('cmn > page before'),
        prev_title: t('cmn > page before'),
        next: t('cmn > next page'),
        next_title: t('cmn > next page'),
      },
    };
    return datalanguage;
  };

  useEffect(() => {
    // roadListData가 존재하고, roadInputList가 변경되지 않았을 때만 업데이트
    console.log('roadListData');
    console.log(roadListData); // API에서 가져온 접근로 목록 상태값에 저장해놓음 (추후 수정용)
    const data = Array.isArray(roadListData) ? roadListData : roadListData?.data;
    //alert('useEffect roadInputListChange 로드입력 값변경')

    if (data) {
      const updatedData = data.map((item) => {
        const directions = (item.incoming_direction || '').split('|');

        // direction 서브 필드 생성
        const directionSubs: Record<string, string> = {};
        for (let i = 1; i <= 10; i++) {
          directionSubs[`incoming_direction_sub${i}`] = directions[i - 1] || '';
        }

        return {
          ...item,
          is_new: false,
          ...directionSubs,
        };
      });

      setRoadInputList(updatedData);
      setSiteInputFormValues((prev) => ({ ...prev, number_road: updatedData.length }));
    } else {
      setRoadInputList([]);
    }
  }, [roadListData]); // roadListData가 변경될 때만 실행

  //add dynamic input form of road
  const addDynamicGroup = () => {
    //이것도, 수정시에 박스 생성인지... (그럼 사이트 아이디를 정보에 포함시켜야하고)
    //최초 생성시에 박스 생성인지... (그럼 사이트 정보는 아직없는것)
    //isNewClicked로 신규 추가인지 아닌지 구분할수있겠다.

    console.log('addDynamicGroup');
    console.log(roadInputList);
    console.log(siteInputFormValues.number_road);

    const number_road = siteInputFormValues.number_road + 1;
    setSiteInputFormValues({
      ...siteInputFormValues,
      number_road: number_road,
    });

    let siteId: number = -1;
    if (isNewClicked === true) {
      //신규 생성이면
      siteId = -1;
    } else {
      //아닐경우
      siteId = siteInputFormValues.site_id;
    }

    setRoadInputList([
      ...roadInputList,
      {
        site_id: siteId, //사이트 id
        road_id: 0, //접근로 id
        name: '', //접근로 명칭 road_name
        incoming_compass: '103001', //진입 방향
        outgoing_compass: '103002', //진출 방향
        incoming_lane_cnt: 2, //진입 방향 차선수
        outgoing_lane_cnt: 2, //진출 방향 차선수
        incoming_direction_sub1: '', //진입방향1
        incoming_direction_sub2: '', //진입방향2
        incoming_direction_sub3: '', //진입방향3
        incoming_direction_sub4: '', //진입방향4
        incoming_direction_sub5: '', //진입방향5
        incoming_direction_sub6: '', //진입방향6
        incoming_direction_sub7: '', //진입방향7
        incoming_direction_sub8: '', //진입방향8
        incoming_direction_sub9: '', //진입방향9
        incoming_direction_sub10: '', //진입방향10
        incoming_direction: '', //방향 모두 합친 값
        crosswalk: '105002', //횡단보도 유무 - 105002
        crosswalk_length: null, //횡단보도 길이
        crosswalk_width: null, //횡단보도 폭
        traffic_light: '106002', //보행자 신호등 유무 - 106002
        mapped_detector: '', //매핑 검지기
        // mapped_vms: '', //매핑 전광판 - 비활성
        mapped_logojector: '', //매핑 로고젝터
        mapped_speaker: '', //매핑 스피커
        is_new: true, //동적박스 생성시에는 새로 추가되는 데이터다
      },
    ]);
  };

  // road list 삭제
  const deleteDynamicGroup = (
    mode: string,
    road_id: number | undefined,
    index: number,
    _reloadCallback: () => void
  ) => {
    //alert('roadList delete')
    //roadList delete button
    console.log(`입력 모드:${mode}`);
    console.log(`접근로 아이디:${road_id}`);
    console.log(`삭제할 인덱스 번호:${index}`);
    if (mode === 'input_mode') {
      //alert(road_id+" 번 접근로 단순 입력 목록 삭제")
      //alert('삭제할 인덱스 번호:'+index)

      // 특정 인덱스를 제외한 새로운 배열 만들기
      // 특정 인덱스를 제외한 새로운 배열 만들기
      const updatedRoadInputList = roadInputList.filter((_, i) => i !== index);

      // 상태 업데이트
      setRoadInputList(updatedRoadInputList);
      console.log(updatedRoadInputList);

      //delete - decrease number-road
      const number_road = siteInputFormValues.number_road - 1;
      setSiteInputFormValues({
        ...siteInputFormValues,
        number_road: number_road,
      });
    } else if (mode === 'list_mode') {
      //상세보기 목록 모드에서도
      //기존데이터 + 새로 입력한 데이터 섞여있으므로 is_new로 검사해서
      //삭제 API날릴 데이터인지, 단순히 박스만 삭제할지

      const roadToDelete = roadInputList[index];
      if (!roadToDelete) return;

      //check if there is mapped_logojector, speaker, ...
      if (
        roadToDelete.mapped_detector ||
        roadToDelete.mapped_logojector ||
        roadToDelete.mapped_speaker
      ) {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(
          t(
            '해당 사이트에 매핑된 [#검지기 or 로고젝터 or 스피커] 이(가) 존재합니다. 먼저 매핑을 해제해주세요'
          )
        );
        return;
      }

      if (roadToDelete.is_new === false && road_id != null) {
        //기존에 존재하던 데이터면 삭제 API
        deleteRoad(road_id);
      }
      const updatedRoadInputList = roadInputList.filter((_, i) => i !== index);
      // 상태 업데이트
      setRoadInputList(updatedRoadInputList);

      //delete - decrease number-road
      const number_road = siteInputFormValues.number_road - 1;
      setSiteInputFormValues({
        ...siteInputFormValues,
        number_road: number_road,
      });

      //reloadCallback()
    }
  };

  // select on change - intersection and crosswalk
  const handleSiteInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    console.log('handleSiteInputChange');
    console.log(name);
    console.log(value);

    setSiteInputFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));

    if (isNewClicked) {
      //new버튼 클릭했을때만
      console.log('생성버튼을 누른상태에서 입력변화');
      setHasChangesCreate(true); //
      // setHasChangesUpdate(false); //생성시에는 변경 되지않았다고 표시 (저장 비활성화)
      disableConfirmButtons(); //생성시에는 register만 하면되고 저장(수정)은 필요없다.
      enableCancelButtons();
    } else {
      setHasChangesUpdate(true);
    }
    //setHasChangesUpdate(true);
  };

  //const tbRefInit = useRef(null);

  //진입, 진출방향 상태값
  const handleRoadInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    _mode: string,
    index: number
  ) => {
    console.log('접근로 정보 입력 change');
    const name = e.target.name;
    const value = e.target.value;
    console.log(name);
    console.log(value);

    if (name === 'outgoing_compass') {
      //alert('진출 방향 선택중')
      //진출방향 선택하면 진입방향 검사하여 둘의 일치여부 확인
      const inComingCompass = roadInputList[index]?.incoming_compass;
      const outGoingCompass = value;

      if (outGoingCompass === inComingCompass) {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t('진출방향과 진입방향은 일치하면 안됩니다. 다시선택하세요!'));
        return;
      }
    } else if (name === 'incoming_compass') {
      const inComingCompass = value;
      const outGoingCompass = roadInputList[index]?.outgoing_compass;

      if (outGoingCompass === inComingCompass) {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t('진출방향과 진입방향은 일치하면 안됩니다. 다시선택하세요!'));
        return;
      }
    }

    // 복사본을 만들어서 해당 index에 있는 원소를 업데이트
    const updatedRoadInputList = [...roadInputList]; // 기존 리스트 복사

    updatedRoadInputList[index] = {
      ...updatedRoadInputList[index], // 해당 index의 기존 데이터 복사
      [name]: value, // 변경된 값을 넣어줌
    };

    console.log('updatedRoadInputList111');
    console.log(updatedRoadInputList);
    //setRoadInputList(updatedRoadInputList);

    // 'incoming_direction_sub1' 부터 'incoming_direction_sub6'까지 값을 검사하여 '|'로 합침
    // 10개까지 존재하므로 10개까지 준비
    const directionFields = [
      'incoming_direction_sub1',
      'incoming_direction_sub2',
      'incoming_direction_sub3',
      'incoming_direction_sub4',
      'incoming_direction_sub5',
      'incoming_direction_sub6',
      'incoming_direction_sub7',
      'incoming_direction_sub8',
      'incoming_direction_sub9',
      'incoming_direction_sub10',
    ];

    // 접근방향 1~6 검사하여 - 값이 채워진 것들만 필터링하여 합침
    // 좌|직 , 좌|직|직  , 좌,직|직,좌|직 , ....
    const currentRoad = updatedRoadInputList[index];
    const incomingDirectionValues = currentRoad
      ? directionFields
          .map((field) => currentRoad[field as keyof RoadInputType])
          .filter((value) => value !== '' && value !== null && value !== undefined)
      : []; // 빈 값 제외

    // 'incoming_direction'에 '|'로 합친 값 넣기
    if (incomingDirectionValues.length > 0 && currentRoad) {
      currentRoad.incoming_direction = incomingDirectionValues.join('|');
    } else {
      //updatedRoadInputList[index].incoming_direction = ''; // 값이 없으면 빈 문자열
    }


    // 변경된 리스트를 상태에 반영
    setRoadInputList(updatedRoadInputList);

    if (isNewClicked) {
      //new버튼 클릭했을때만
      setHasChangesCreate(true);
      disableConfirmButtons(); //생성시에는 register만 하면되고 저장(수정)은 필요없다.
    } else {
      setHasChangesUpdate(true);
    }
  };

  // 테이블 row선택시
  const handleRowSelected = useCallback(
    (row: RowComponent) => {
      const rowData = row.getData();
      // 같은 행 재클릭 시 무시
      if (selectedSiteIdRef.current?.site_id === rowData.site_id) {
        return;
      }

      setRoadInputList([]);
      const rowNum = row.getPosition(); // DOM에서 직접 행 번호를 가져오기


      //선택된 행 번호를 저장해놓음 (선택,해제 할때 쓰기 위하여)
      setCurRowId(rowNum);

      //수정작업을 대비하여
      //site - 현재 값들을 siteInpuForm에 넣어놓기
      //road - 현재 값들을 roadInputList에 넣어놓기

      if (hasChangesCreateRef.current || hasChangesUpdateRef.current) {
        {
          // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
          const message = new NoticeMessage(
            t('입력하신 내용이 저장되지 않습니다. 계속하시겠습니까?'),
            {
              mode: 'confirm',
            }
          );
          message
            .confirmClicked()
            .then(() => {
              tbRef.current?.deselectRow();
              row.select();
              row.getElement().classList.add('custom-hover');

              const rowData = row.getData();
              // const siteId = rowData.site_id;
              // setSiteId(siteId);
              setSelectedSiteId({
                site_id: rowData.site_id,
              });

              setSiteInputFormValues((prevValues) => ({
                ...prevValues,
                site_id: rowData.site_id,
                name: rowData.name,
                address: rowData.address,
                lat: rowData.lat,
                lng: rowData.lng,
                type: rowData.type,
                number_road: rowData.number_road || 0,
                mapped_box: rowData.mapped_box || '',
                description: 'description',
              }));

              setDisabledForm(false);
              setHasChangesUpdate(false);
              setHasChangesCreate(false);
              enableUpdateButtons();
              setIsNewClicked(false);
            })
            .catch(() => {
              row.deselect();
              const prevId = selectedSiteIdRef.current?.site_id;
              if (prevId) {
                const prevRow = tbRef.current?.getRow(prevId);
                prevRow?.select();
                prevRow?.getElement().classList.add('custom-hover');
                // setDisabledForm(false);
              }
            });
        }
      } else {
        tbRef.current?.deselectRow();
        row.select();
        row.getElement().classList.add('custom-hover');

        setDisabledForm(false);
        setHasChangesUpdate(false);
        enableUpdateButtons();
        setIsNewClicked(false);
        //setGroupList([])

        const rowData = row.getData();
        setSiteId(rowData.site_id);

        setSelectedSiteId({
          site_id: rowData.site_id,
        });

        setSiteInputFormValues((prevValues) => ({
          ...prevValues,
          site_id: rowData.site_id,
          name: rowData.name,
          address: rowData.address,
          lat: rowData.lat,
          lng: rowData.lng,
          type: rowData.type,
          number_road: rowData.number_road || 0,
          mapped_box: rowData.mapped_box || '',
          description: 'description',
        }));
      }
    },
    [enableUpdateButtons, t]
  );

  const tbRef = useRef<Tabulator | null>(null);
  const nameRef = useRef<HTMLElement>(null);
  const [disabledForm, setDisabledForm] = useState(true);
  const [_disabledId, setDisabledId] = useState(true);
  const [hasChangesUpdate, setHasChangesUpdate] = useState(false);
  const [hasChangesCreate, setHasChangesCreate] = useState(false);
  const [_isRequired, setIsRequired] = useState(false);
  const hasChangesUpdateRef = useRef(hasChangesUpdate);
  const hasChangesCreateRef = useRef(hasChangesCreate);
  const [isNewClicked, setIsNewClicked] = useState(false);
  const isNewClickedRef = useRef(isNewClicked);

  // Add Use Effect to update the ref value
  useEffect(() => {
    hasChangesUpdateRef.current = hasChangesUpdate;
    hasChangesCreateRef.current = hasChangesCreate;
    selectedSiteIdRef.current = selectedSiteId;
    isNewClickedRef.current = isNewClicked;
  }, [hasChangesUpdate, hasChangesCreate, selectedSiteId, isNewClicked]);

  const handleSearch = useCallback(
    (value: string | null = null) => {
      //alert('검색!')
      const resultInput = value ? `input=${value}` : '';
      //alert(resultInput)//검색어
      const result = resultInput;
      setQueryParams(result);
      //검색후 하단 박스 초기화
      enableInitialButtons();
      emptyDetail();
      //alert('empty road')
      setRoadInputList([]);
      reloadCallback();
    },
    [emptyDetail, enableInitialButtons, reloadCallback]
  );

  const handleReset = () => {
    setQueryParams('');
  };

  const handleNewButtonClick = () => {
    if (hasChangesCreate || hasChangesUpdate) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), {
        mode: 'confirm',
      });
      message.confirmClicked().then(() => {
        tbRef.current?.deselectRow();
        emptyDetail();
        setDisabledForm(false);
        // disableConfirmButtons();
        setSelectedSiteId({ site_id: null });
        setSiteId(null);
        // disableDeleteButtons();
        setIsNewClicked(true);
        enableCancelButtons();
        setHasChangesUpdate(false);
        setRoadInputList([]);
      });
    } else {
      tbRef.current?.deselectRow();
      emptyDetail();
      setDisabledForm(false);
      setSelectedSiteId({ site_id: null });
      // disableConfirmButtons();
      setSiteId(null);
      // disableDeleteButtons();
      setIsNewClicked(true);
      enableCancelButtons();
      setHasChangesUpdate(false);
      setRoadInputList([]);
    }
  };

  //등록버튼 클릭시
  const handleRegistButtonClick = async () => {
    //alert('regist!');
    // 입력폼 검사
    //alert('등록')
    const isSiteRoadInputFormValid = siteRoadInputFormCheck();
    if (!isSiteRoadInputFormValid) {
      //alert('입력검사 통과못함')
      return; // 입력검사 통과 못하면 끝냄
    }

    const createSiteFormValues = {
      ...siteInputFormValues,
      description: siteInputFormValues.description ?? '',
    };

    //const siteRoadInfo={siteInfo:createSiteFormValues,roadInputList:roadInputList}
    //createSiteRoad(siteRoadInfo)
    //site추가한다음 아이디를 받아서 해야해서... 한번에 처리하는게 불가능할듯
    //site먼저 처리하고 road를 처리할수밖에
    //createSite -> get site id -> create road with site id
    try {
      // createSite 호출 후, 결과를 처리하는 방법
      createSite(createSiteFormValues, {
        onSuccess: (createSiteResult) => {

          // site추가 후 반환된 site_id를 사용해야함
          const site_id = createSiteResult?.data?.site_id; // createSiteResult에서 site_id를 가져옴

          if (!site_id) {
            throw new Error('사이트 ID가 없습니다.');
          }

          // road 정보는 여러 개이므로 요청을 반복해서 전송해야 함

          // Only create road records for crossroad sites; non-crossroad sites
          // never expose the 접근로 UI, so any roadInputList state is residual
          // from a prior type selection and is intentionally ignored here.
          if (isCrossroad) {
            // 각 roadItem에 대해 createRoad 요청을 비동기적으로 전송
            for (const roadItem of roadInputList) {
              createRoad({ ...roadItem, name: roadItem.name ?? '', site_id } as unknown as RoadData); // 각 요청을 비동기적으로 처리
            }
          }

          //selectedSite set
          setSelectedSiteId({
            site_id: site_id,
          });

          setSiteId(site_id);
          setNewId(site_id);
          // setIsNewClicked(false);
          setHasChangesCreate(false);

          //alert('등록이 완료되었습니다!');
          //등록후 입력폼 초기화 - init site, road form
          // InitSiteRoadInputForm();

          // reloadCallback();
          //emptyDetail()
        },
        onError: (error) => {
          console.error('createSite onError:', error);
          //alert('사이트 등록 중 오류가 발생했습니다. 다시 시도해주세요.');
        },
      });
    } catch (_error) {
      //alert('오류가 발생했습니다. 다시 시도해주세요.');
    }
  };

  const siteRoadInputFormCheck = () => {
    //사이트 정보, 접근로 정보 입력필드 검사 함수
    //alert('검사')
    // 제외할 필드들
    const excludedFields = [
      'site_id',
      'type',
      'number_road',
      'mapped_box',
      'mapped_sensor',
      'description',
      'registered_by',
      'registered_time',
      'updated_by',
      'updated_time',
    ]; //입력검사시에 제외할 필드 - site check exclude field
    //사이트 아이디는 자동으로 생성됨, 함체정보는 입력하지 않음

    // 사이트정보 입력 폼검사 - 제외할 필드를 제외한 나머지 필드들만 검사
    const emptyFields = Object.keys(siteInputFormValues)
      .filter((key) => !excludedFields.includes(key)) // 제외할 필드를 필터링
      .filter(
        (key) =>
          (siteInputFormValues as unknown as Record<string, unknown>)[key] === null ||
          (siteInputFormValues as unknown as Record<string, unknown>)[key] === ''
      ); // 비어있는 필드만 필터링

    // 비어있는 필드 출력
    if (emptyFields.length > 0) {
      // 비어있는 필드들의 이름을 알림으로 띄우거나 처리
      const siteFieldName = {
        name: '명칭',
        lat: '위도',
        lng: '경도',
        address: '주소',
      };
      // 빈 필드에 대한 메시지 생성
      const _emptyFieldName = emptyFields
        .map((field) => `${(siteFieldName as Record<string, string>)[field]}`)
        .join(', ');
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('필수 값을 모두 입력해주세요'));
      return false; // 비어있는 필드가 있으면 false 리턴
    } else {
    }

    // Skip road validation when the site is not a crossroad: the 접근로 section
    // is hidden in the UI and any roadInputList state is intentionally ignored
    // when saving.
    if (!isCrossroad) {
      return true;
    }

    //접근로 입력 갯수만큼 생성된 동적박스에 입력값이 모두 채워졌는지도 검사해야한다.
    const roadFieldName = {
      name: '명칭',
      incoming_direction_sub1: '진입 진행방향1',
      incoming_direction_sub2: '진입 진행방향2',
      incoming_direction_sub3: '진입 진행방향3',
      incoming_direction_sub4: '진입 진행방향4',
      incoming_direction_sub5: '진입 진행방향5',
      incoming_direction_sub6: '진입 진행방향6',
      incoming_direction_sub7: '진입 진행방향7',
      incoming_direction_sub8: '진입 진행방향8',
      incoming_direction_sub9: '진입 진행방향9',
      incoming_direction_sub10: '진입 진행방향10',
      crosswalk_length: '횡단 보도 길이',
      crosswalk_width: '횡단 보도 폭',
    };
    var emptyRoadFieldName = '';
    const isRoadFieldsFilled = roadInputList?.every((item, index) => {
      //incoming_lane_cnt

      return Object.entries(item).every(([key, value]) => {
        // exclude filed dont check - 제외할 필드는 체크하지 않음
        // like input - disabled={true}
        var excludedFields = [
          'site_id',
          'road_id',
          'mapped_detector',
          'mapped_logojector',
          'mapped_speaker',
          'mapped_floodlight',
          'mapped_gobo',
          'incoming_direction',
          'incoming_compass',
          'outgoing_compass',
          'incoming_lane_cnt',
          'outgoing_lane_cnt',
          'crosswalk',
          'traffic_light',
          'registered_by',
          'registered_time',
          'updated_by',
          'updated_time',
          'is_new',
        ];

        if (item.crosswalk === '105002') {
          //미존재할경우 -횡단보도 길이,폭 제외에 추가
          excludedFields.push('crosswalk_width', 'crosswalk_length');
        } else if (item.crosswalk !== '105002') {
          //존재할 경우 - 제외 항목에서 제거
          excludedFields = excludedFields.filter(
            (field) => field !== 'crosswalk_width' && field !== 'crosswalk_length'
          );
        }

        const incomingLaneCnt = Number(item.incoming_lane_cnt) || 0;
        // incoming_direction_sub 갯수에 따라서 제외할 항목 변동
        for (let i = incomingLaneCnt + 1; i <= 10; i++) {
          excludedFields.push(`incoming_direction_sub${i}`);
        }

        //접근방향 1,2,3,4,5,6 은 모두 채우지 않아도 상관없다. 일단 1,2만 필수 3,4,5,6은 제외시켜둠
        //접근방향 모두 합친값(incoming_direction)은 입력할때마다 새로 갱신
        //road check exclude field

        // exclude filed dont check - 제외할 필드는 체크하지 않음
        if (excludedFields.includes(key)) {
          return true;
        }

        // 빈 값, null, undefined 확인
        if (value === '' || value === null || value === undefined) {
          console.log(`Row ${index + 1}: 필드 "${key}" 가 비어 있습니다.`); // 어떤 필드가 비어있는지 출력
          emptyRoadFieldName = `${index + 1}번째 ${(roadFieldName as Record<string, string>)[key]}`;
          return false;
        }

        return true;
      });
    });

    console.log('모든 값이 채워졌는지:', isRoadFieldsFilled);
    console.log('excludedFields');
    console.log(excludedFields);

    if (isRoadFieldsFilled) {
    } else {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(`필수 값을 모두 입력해주세요(접근로): ${emptyRoadFieldName}`));
      return false;
    }
    //input form fully filled completed - 입력이 모두 완료되었으면
    // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
    //new NoticeMessage(t('모든 필드가 올바르게 입력되었습니다.'));

    return true;
  };

  const handleConfirmButtonClick = () => {
    //alert('confirm!')
    //alert('확인!')
    const isSiteRoadInputFormValid = siteRoadInputFormCheck();
    if (!isSiteRoadInputFormValid) {
      //alert('입력검사 통과못함')
      return; // 입력검사 통과 못하면 끝냄
    }

    const updatedSiteInputFormValues = {
      ...siteInputFormValues,
    };
    setSiteInputFormValues({ ...siteInputFormValues });

    //alert('변경될값 확인')
    const _siteId = updatedSiteInputFormValues.site_id;

    setDisabledForm(false);
    setHasChangesUpdate(false);
    enableUpdateButtons();
    setIsNewClicked(false);

    const siteRoadInfo = {
      siteInfo: updatedSiteInputFormValues,
      // When the site is not a crossroad, road records are intentionally left
      // untouched in the DB; passing an empty list keeps existing roads as-is.
      roadInputList: isCrossroad ? roadInputList : [],
    };

    updateSiteRoad(siteRoadInfo);
  };

  const handleCancelButtonClick = () => {
    if (hasChangesUpdate) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), {
        mode: 'confirm',
      });

      message.confirmClicked().then(() => {
        tbRef.current?.deselectRow();
        emptyDetail();
        setDisabledForm(true);
        setDisabledId(true);
        enableInitialButtons();
        setHasChangesUpdate(false);
        setSiteId(null);
        setRoadInputList([]);
        reloadCallback();
      });
    } else if (isNewClicked) {
      if (hasChangesCreate) {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        const message = new NoticeMessage(t('msg > flush confirm'), {
          mode: 'confirm',
        });

        message.confirmClicked().then(() => {
          tbRef.current?.deselectRow();
          emptyDetail();
          setDisabledForm(true);
          setDisabledId(true);
          enableInitialButtons();
          setHasChangesUpdate(false);
          setSiteId(null);
          setHasChangesCreate(false);
          setRoadInputList([]);
        });
      } else {
        tbRef.current?.deselectRow();
        emptyDetail();
        setDisabledForm(true);
        setDisabledId(true);
        enableInitialButtons();
        setHasChangesUpdate(false);
        setHasChangesCreate(false);
        setSiteId(null);
      }
    } else {
      reloadCallback();
      setDisabledForm(true);
      emptyDetail();
      setSiteId(null);
    }
  };

  const handleDeleteButtonClick = () => {
    //alert('delete!')
    // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
    const message = new NoticeMessage(t('정말로 삭제하시겠습니까?'), {
      mode: 'confirm',
    });
    message.confirmClicked().then(() => {
      if (siteInputFormValues?.mapped_box) {
        //check if there is mapped_box
        // const { mapped_box } = siteInputFormValues;
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t('해당 사이트에 매핑된 함체가 존재합니다.먼저 매핑을 해제해주세요'));
        return;
      }

      //alert('삭제 진행!')
      const siteId = siteInputFormValues?.site_id;
      const roadIdList = roadInputList.map((item) => item.road_id);
      console.log(roadIdList);
      const siteRoadInfo = { siteId: siteId, roadIdList: roadIdList };

      deleteSiteRoad(siteRoadInfo);
    });
  };

  const { mapped_box, mapped_sensor } = detailSiteData?.data
    ? detailSiteData.data
    : { mapped_box: null, mapped_sensor: null };
  //매핑함체는 사이트 상세정보 API에서 가져와야함

  // Show the sensor mapping textbox and the 접근로 section only for
  // crossroad-with-crosswalk sites; other site types don't have access roads.
  const isCrossroad = siteInputFormValues.type === CROSSROAD_TYPE_CODE;

  //logData
  useEffect(() => {
    if (detailSiteData?.data) {
      setSiteInputFormValues((prev) => ({
        ...prev,
        ...detailSiteData.data,
        number_road: prev.number_road, // detail API에 number_road 없으므로 기존 값 유지
      }));
    }
  }, [detailSiteData]);

  useEffect(() => {
    if (newId && siteListData && siteListData?.data?.length > 0) {
      const newRow = siteListData?.data.find((item) => item.site_id === newId);
      if (newRow) {
        const row = tbRef.current?.getRow(newId);
        if (row) {
          row.select();
          row.getElement().classList.add('custom-hover');
          handleRowSelected(row);
          setSelectedSiteId({ site_id: newId });
          setNewId(null); // reset so it doesn't keep re-triggering
        }
      }
    }
  }, [siteListData, newId, handleRowSelected]);

  const logData = detailSiteData?.data
    ? [
        {
          label: t('cmn > registered by'),
          value: detailSiteData?.data.registered_by || '',
        },
        {
          label: t('cmn > registered time'),
          value: detailSiteData?.data.registered_time || '',
        },
        {
          label: t('cmn > updated by'),
          value: detailSiteData?.data.updated_by || '',
        },
        {
          label: t('cmn > updated time'),
          value: detailSiteData?.data.updated_time || '',
        },
      ]
    : [];

  return (
    <section className="wrap">
      <div className="header-title">
        <h3>사이트 관리</h3>
        <h3>&gt;</h3>
        <h3>사이트 속성 관리</h3>
      </div>

      <ContainerCard>
        <Filtering
          placeholder="사이트ID / 명칭 / 주소"
          disableFiltering={true}
          onSearch={handleSearch}
          onReset={handleReset}
        ></Filtering>
      </ContainerCard>

      <ContainerCard>
        <ReactTabulator
          id="site-management-table"
          data={siteListData?.data || []}
          columns={boxTabulator}
          layout={'fitColumns'}
          className="tabulator-custom w-full "
          //   pagination="local"
          options={{
            ...optionsTabulator(languageTabulator, siteListData?.data.length || 0),
            initialSort: [
              { column: 'updated_time', dir: 'desc' }, // 내림차순으로 'updated_time' 컬럼 초기 정렬
            ],
          }}
          onRef={(r) => {
            tbRef.current = r.current;
          }}
          events={{
            rowClick: (_e: UIEvent, row: RowComponent) => handleRowSelected(row),
            tableBuilt: () => {
              const selectedSiteId = selectedSiteIdRef.current?.site_id;

              if (selectedSiteId) {
                tbRef.current?.getRows().forEach((row) => {
                  const rowData = row.getData();
                  if (rowData.site_id === selectedSiteId) {
                    row.select();
                    row.getElement().classList.add('custom-hover');
                  }
                });
              } else if (newId) {
                const row = tbRef.current?.getRow(newId);
                if (row) {
                  tbRef.current?.scrollToRow(row);
                  row.select();
                  row.getElement().classList.add('custom-hover');
                  handleRowSelected(row);
                  setSelectedSiteId({ site_id: newId });
                }
              }
            },
          }}
        />
      </ContainerCard>

      <ContainerCard className=" flex flex-col">
        <div className="gap-2.5 flex flex-col">
          <span className="title2bold ">사이트</span>
          {/* <hr className="border-t border-gray-300" /> */}
        </div>

        <div className="box-management-col flex flex-col gap-3">
          <div className="grid grid-cols-3 gap-[50px]">
            <DetailForm
              className="items-center!"
              label="사이트 ID"
              onChange={handleSiteInputChange}
              name="site_id"
              value={siteInputFormValues.site_id || ''}
              disabled={true}
              inputType="text"
            />

            <DetailForm
              className="items-center!"
              label="명칭"
              required={true}
              placeholder=""
              onChange={handleSiteInputChange}
              name="name"
              maxLength={100}
              value={siteInputFormValues.name || ''}
              disabled={disabledForm}
              inputType="text"
            />

            <DetailForm
              className="items-center!"
              label="주소"
              required={true}
              placeholder=""
              onChange={handleSiteInputChange}
              name="address"
              value={siteInputFormValues.address || ''}
              disabled={disabledForm}
              inputType="text"
            />
          </div>

          <div className="grid grid-cols-3 gap-[50px]">
            <div className="flex w-full flex-row gap-x-4">
              <DetailForm
                className="items-center!"
                label="위도 / 경도"
                required={true}
                showInput={false}
                inputType="text"
              >
                <div className="flex w-full flex-row gap-x-2">
                  <GeneralInput
                    customInput="w-full"
                    placeholder=""
                    onChange={handleSiteInputChange}
                    name="lat"
                    maxLength={10}
                    pattern="^[0-9]*\.?[0-9]*$" // number and . - 숫자와 온점만 허용하는 정규식
                    value={siteInputFormValues.lat || ''}
                    disabled={disabledForm}
                  />
                  <GeneralInput
                    customInput="w-full"
                    placeholder=""
                    onChange={handleSiteInputChange}
                    name="lng"
                    maxLength={10}
                    pattern="^[0-9]*\.?[0-9]*$" // number and . - 숫자와 온점만 허용하는 정규식
                    value={siteInputFormValues.lng || ''}
                    disabled={disabledForm}
                  />
                </div>
              </DetailForm>
            </div>

            <DetailForm
              inputType="select"
              className="items-center!"
              label="타입"
              required={true}
              optionSelect={typeOptions} // Dynamic options based on site_type
              onChange={handleSiteInputChange}
              name="type"
              value={siteInputFormValues.type || ''}
              disabled={disabledForm}
            />

            <DetailForm
              className="items-center!"
              label="접근로 수"
              onChange={handleSiteInputChange}
              name="number_road"
              inputType={'text'}
              pattern={/^[0-9]*$/}
              maxLength={2}
              value={siteInputFormValues.number_road || ''}
              disabled={true} //road_number input is disabled
            />
          </div>

          <div className="grid grid-cols-3 gap-[50px]">
            <DetailForm
              className="items-center!"
              label="매핑 함체"
              placeholder=""
              onChange={handleSiteInputChange}
              name="mapped_box"
              disabled={true}
              value={mapped_box ?? ''}
              inputType="text"
            />
            <DetailForm
              className="items-center!"
              label="매핑 센서"
              placeholder=""
              onChange={handleSiteInputChange}
              name="mapped_sensor"
              disabled={true}
              value={mapped_sensor ?? ''}
              inputType="text"
            />
          </div>

          {isCrossroad && (
            <>
              <hr className="border-t border-gray-300" />

              <div className="gap-2.5 flex flex-row items-center">
                <span className="title2bold ">접근로</span>
                <button
                  type="button"
                  className={`body1bold px-[20px] py-[3px] text-[#FEFEFE] cursor-pointer
                      ${
                        isNewClicked === true || siteId != null
                          ? 'bg-[#3D6B85] hover:opacity-80'
                          : 'bg-[#D3D3D3] cursor-not-allowed opacity-50'
                      }`}
                  onClick={isNewClicked || siteId != null ? addDynamicGroup : () => {}}
                >
                  Add +
                </button>
                {/* <hr className="border-t border-gray-300" /> */}
              </div>

              {roadInputList?.map((data, index) => (
            <div key={`road-${index}`}>
              <DynamicForm
                index={index}
                onDelete={() => {
                  if (isNewClicked) {
                    deleteDynamicGroup('input_mode', data.road_id, index, reloadCallback);
                    //input_mode -> just delete box
                  } else {
                    deleteDynamicGroup('list_mode', data.road_id, index, reloadCallback);
                    //liste_mode -> delete API + just delete box
                  }
                }}
                handleRoadInputChange={(e) => handleRoadInputChange(e, 'input_mode', index)}
                road_id={String(data.road_id || '')}
                name={data.name || ''} //접근로 이름
                crosswalk_length={String(data.crosswalk_length || '')}
                crosswalk_width={String(data.crosswalk_width || '')}
                traffic_light={data.traffic_light || ''}
                incoming_direction={data.incoming_direction || ''}
                incoming_direction_sub1={data.incoming_direction_sub1 || ''}
                incoming_direction_sub2={data.incoming_direction_sub2 || ''}
                incoming_direction_sub3={data.incoming_direction_sub3 || ''}
                incoming_direction_sub4={data.incoming_direction_sub4 || ''}
                incoming_direction_sub5={data.incoming_direction_sub5 || ''}
                incoming_direction_sub6={data.incoming_direction_sub6 || ''}
                incoming_direction_sub7={data.incoming_direction_sub7 || ''}
                incoming_direction_sub8={data.incoming_direction_sub8 || ''}
                incoming_direction_sub9={data.incoming_direction_sub9 || ''}
                incoming_direction_sub10={data.incoming_direction_sub10 || ''}
                site_id={String(data.site_id || '')}
                crosswalk={data.crosswalk || ''} //횡단보도 유무
                incoming_compass={data.incoming_compass || ''} //진입방향
                outgoing_compass={data.outgoing_compass || ''} //진출방향
                incoming_lane_cnt={Number(data.incoming_lane_cnt) || 0} //진입 차선수
                outgoing_lane_cnt={Number(data.outgoing_lane_cnt) || 0} //진출 차선수
                mapped_detector={data.mapped_detector || ''}
                // mapped_vms={data.mapped_vms || ''} // 전광판 비활성
                mapped_logojector={data.mapped_logojector || ''}
                mapped_speaker={data.mapped_speaker || ''}
                directionOptions={directionOptions || []}
                crosswalkPresenceOptions={crosswalkPresenceOptions || []}
                pedestrianSignalPresenceOptions={pedestrianSignalPresenceOptions || []}
              />
            </div>
          ))}
            </>
          )}

          <hr className="border-t border-gray-300" />
          <div className="flex items-center justify-between gap-4 w-full">
            <div className="flex-1">{disabledForm ? null : <LogList logs={logData} />}</div>
            <div className="flex-none">
              <ButtonGroup
                onClickDelete={handleDeleteButtonClick}
                onClickNew={handleNewButtonClick}
                onClickCancel={handleCancelButtonClick}
                onClickRegist={handleRegistButtonClick}
                //onClickRegist={handleRegistButtonClickTest}
                onClickConfirm={handleConfirmButtonClick}
                isNewClicked={isNewClicked}
                cancelButtonState={buttonState.cancel}
                confirmButtonState={hasChangesUpdate ? false : buttonState.confirm}
                deleteButtonState={buttonState.delete}
                newButtonState={hasChangesCreate ? false : buttonState.create}
              />
            </div>
          </div>
        </div>
      </ContainerCard>
    </section>
  );
};

export default SiteManagement;
